<template>
  <div class="my-notes-page">
    <header ref="myNotesFixedBar" class="my-notes-page__fixed-bar">
      <div class="my-notes-page__top-bar">
        <h1 class="my-notes-page__title">我的笔记</h1>
      </div>

      <section v-if="labelTabs.length > 1" class="tags-strip">
        <div class="tags-strip__scroll">
          <button
            v-for="item in labelTabs"
            :key="item.value"
            type="button"
            class="tag-btn"
            :class="{ active: activeLabel === item.value }"
            @click="changeLabel(item.value)"
          >
            {{ item.label }}
            <span v-if="item.count != null" class="tag-btn__count">{{ item.count }}</span>
          </button>
        </div>
      </section>
    </header>

    <div
      class="my-notes-page__fixed-spacer"
      :style="{ height: fixedBarHeight + 'px' }"
      aria-hidden="true"
    />

    <section class="list-wrap">
      <article
        v-for="note in displayNotes"
        :key="note.id"
        class="summary-card"
        @click="openNote(note)"
      >
        <div class="card-top">
          <span class="badge">{{ noteLabel(note) }}</span>
          <span class="date">{{ formatDate(note.updateDate || note.createDate) }}</span>
        </div>
        <h3>{{ note.theme || '未命名总结' }}</h3>
        <div class="card-content card-content--note">
          {{ preview(note.noteContent) }}
        </div>
      </article>

      <div v-if="loading" class="list-status list-status--loading">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-else-if="!notes.length" class="list-status empty-text">暂无笔记</div>
      <div v-else-if="!displayCount" class="list-status empty-text">该标签下暂无笔记</div>
      <div v-else-if="!hasMore" class="list-status no-more-text">没有更多了</div>
      <div v-if="loadingMore" class="list-status list-status--loading list-status--more">
        <i class="el-icon-loading"></i>
        <span>正在加载更多...</span>
      </div>
      <div ref="loadMoreTrigger" class="load-trigger"></div>
    </section>
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { fetchUserNoteList, notePreview } from '@/api/userNote'

export default {
  name: 'MyNotesPage',
  data() {
    return {
      loading: false,
      loadingMore: false,
      isRequesting: false,
      notes: [],
      pageNum: 1,
      pageSize: 10,
      hasMore: true,
      observer: null,
      fixedBarHeight: 118,
      activeLabel: 'all'
    }
  },
  computed: {
    labelTabs() {
      const counts = new Map()
      this.notes.forEach((note) => {
        const label = this.noteLabel(note)
        counts.set(label, (counts.get(label) || 0) + 1)
      })
      const tabs = [{ label: '全部', value: 'all', count: this.notes.length }]
      Array.from(counts.entries())
        .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'zh'))
        .forEach(([label, count]) => {
          tabs.push({ label, value: label, count })
        })
      return tabs
    },
    displayNotes() {
      if (this.activeLabel === 'all') return this.notes
      return this.notes.filter((note) => this.noteLabel(note) === this.activeLabel)
    },
    displayCount() {
      if (this.activeLabel === 'all') return this.notes.length
      return this.displayNotes.length
    }
  },
  watch: {
    labelTabs(tabs) {
      if (this.activeLabel !== 'all' && !tabs.some((item) => item.value === this.activeLabel)) {
        this.activeLabel = 'all'
      }
    }
  },
  mounted() {
    this.$bus.$on('refresh-my-notes', this.refreshAndScrollToTop)
    this.loadNotes(false)
    this.initObserver()
    this.setupFixedBarMeasure()
  },
  activated() {
    this.setupFixedBarMeasure()
  },
  beforeDestroy() {
    this.$bus.$off('refresh-my-notes', this.refreshAndScrollToTop)
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
    if (this._fixedBarRO) {
      this._fixedBarRO.disconnect()
      this._fixedBarRO = null
    }
  },
  methods: {
    measureFixedBarHeight() {
      const el = this.$refs.myNotesFixedBar
      if (!el) return
      const h = Math.ceil(el.getBoundingClientRect().height)
      if (h > 0) this.fixedBarHeight = h
    },
    setupFixedBarMeasure() {
      this.$nextTick(() => {
        this.measureFixedBarHeight()
        const el = this.$refs.myNotesFixedBar
        if (!el || typeof ResizeObserver === 'undefined') return
        if (this._fixedBarRO) this._fixedBarRO.disconnect()
        this._fixedBarRO = new ResizeObserver(() => this.measureFixedBarHeight())
        this._fixedBarRO.observe(el)
      })
    },
    preview(content) {
      return notePreview(content)
    },
    noteLabel(note) {
      const label = note && note.parentLabel != null ? String(note.parentLabel).trim() : ''
      return label || '通用'
    },
    changeLabel(value) {
      if (this.activeLabel === value) return
      this.activeLabel = value
      this.scrollToTop()
    },
    scrollToTop() {
      const scrollEl = this.$el && this.$el.closest('.content-area')
      if (scrollEl) {
        scrollEl.scrollTop = 0
      } else {
        window.scrollTo(0, 0)
      }
    },
    async refreshAndScrollToTop() {
      this.scrollToTop()
      await this.loadNotes(false)
    },
    formatDate(raw) {
      if (!raw) return ''
      const d = new Date(String(raw).replace(/-/g, '/'))
      if (Number.isNaN(d.getTime())) return String(raw).slice(0, 10)
      return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
    },
    async loadNotes(isLoadMore) {
      if (this.isRequesting) return
      if (isLoadMore && (!this.hasMore || this.loadingMore)) return

      if (isLoadMore) {
        this.loadingMore = true
      } else {
        this.loading = true
        this.pageNum = 1
        this.hasMore = true
        this.notes = []
        this.activeLabel = 'all'
      }

      this.isRequesting = true
      const currentPage = this.pageNum
      try {
        const data = await fetchUserNoteList({
          pageNum: currentPage,
          pageSize: this.pageSize
        })
        const records = Array.isArray(data.records) ? data.records : []
        this.notes = isLoadMore ? [...this.notes, ...records] : records
        this.pageNum = currentPage + 1
        this.hasMore = records.length >= this.pageSize
        if (typeof data.total === 'number') {
          this.hasMore = data.total > currentPage * this.pageSize
        }
      } catch (error) {
        console.error('加载笔记列表失败:', error)
        this.$message.error(getApiErrorMessage(error, '加载笔记列表失败'))
      } finally {
        this.loading = false
        this.loadingMore = false
        this.isRequesting = false
      }
    },
    initObserver() {
      if (!window.IntersectionObserver) return
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && this.hasMore && !this.isRequesting) {
              this.loadNotes(true)
            }
          })
        },
        { root: null, rootMargin: '600px', threshold: 0.01 }
      )
      this.$nextTick(() => {
        if (this.$refs.loadMoreTrigger) {
          this.observer.observe(this.$refs.loadMoreTrigger)
        }
      })
    },
    openNote(note) {
      const summaryId = note && (note.summaryId || note.idEzTextSummary)
      if (!summaryId) {
        this.$message.warning('关联总结不存在')
        return
      }
      this.$router.push({
        path: '/summary-list/detail',
        query: {
          from: 'my-notes',
          id: String(summaryId)
        }
      })
    }
  }
}
</script>

<style scoped>
.my-notes-page {
  min-height: 100%;
  background: #fff;
  padding: 0 0 80px;
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.my-notes-page__fixed-bar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 40;
  box-sizing: border-box;
  background: #fff;
  border-bottom: 1px solid rgba(7, 29, 73, 0.08);
  transform: translateZ(0);
  backface-visibility: hidden;
}

.my-notes-page__fixed-spacer {
  width: 100%;
  flex-shrink: 0;
  pointer-events: none;
}

.my-notes-page__top-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 16px 20px 12px;
  padding-top: calc(16px + env(safe-area-inset-top, 0px));
  background: #fff;
}

.my-notes-page__title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.88);
  letter-spacing: -0.02em;
  text-align: center;
}

.tags-strip {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  padding: 0 16px 12px;
}

.my-notes-page__fixed-bar .tags-strip {
  padding-bottom: 12px;
}

.tags-strip__scroll {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-wrap: nowrap;
  gap: 10px;
  overflow-x: auto;
  padding-bottom: 2px;
  -webkit-overflow-scrolling: touch;
}

.tags-strip__scroll::-webkit-scrollbar {
  display: none;
}

.tag-btn {
  border: none;
  background: #f5f9ff;
  color: #2c4774;
  border-radius: 18px;
  padding: 7px 16px;
  white-space: nowrap;
  font-size: 14px;
  flex-shrink: 0;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tag-btn.active {
  background: #4253ff;
  color: #fff;
}

.tag-btn__count {
  font-size: 12px;
  opacity: 0.85;
}

.list-wrap {
  width: 100%;
  box-sizing: border-box;
  padding: 0 16px;
}

.summary-card {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #eef3ff;
  border-radius: 16px;
  padding: 14px;
  margin: 0 0 12px;
  box-shadow: 0 2px 8px rgba(66, 83, 255, 0.05);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  user-select: none;
  transition: background 0.15s;
}

.summary-card:active {
  background: #f8faff;
}

.card-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.badge {
  display: inline-flex;
  align-items: center;
  height: 22px;
  border-radius: 8px;
  background: #4253ff;
  color: #ffffff;
  padding: 0 8px;
  font-size: 12px;
  flex-shrink: 0;
}

.date {
  font-size: 12px;
  color: #8a9bb8;
  flex-shrink: 0;
}

.summary-card h3 {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #2f47ff;
  line-height: 1.4;
}

.card-content {
  font-size: 14px;
  color: #5d6f94;
  line-height: 1.55;
}

.card-content--note {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.list-status {
  text-align: center;
  padding: 24px 0 8px;
  font-size: 14px;
  color: #8a9bb8;
}

.list-status--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.list-status--more {
  padding-top: 8px;
}

.empty-text,
.no-more-text {
  color: #a8b4cc;
}

.load-trigger {
  width: 100%;
  height: 1px;
  pointer-events: none;
}
</style>
